import { ref, watch, onMounted, onUnmounted, computed, nextTick } from 'vue';
import { MdTypography, MdIconArrowCaret, MdButton, MdIconReload } from '@antiqua-dls/vue-components';
import TextField from 'src/components/form/new-text-field/index.vue'
import { NUMBER_CIRCLE_GLYPH } from 'src/utils/constants/common.js'
import no_result_image from 'src/components/icons/lady-thinking-icon/no_result_image.svg'
import ToggleSwitch from 'src/components/form/toggle-switch/index.vue';

export default {
    props: {
        name: {
            type: String,
            default: 'multiSelect'
        },
        label: {
            type: String,
            default: 'Multi Select'
        },
        options: {
            type: Array,
            required: true,
        },
        modelValue: {
            type: Array,
            default: () => [],
        },
        enableSearch: {
            type: Boolean,
            default: true
        },
        clearSelections: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        externalSelectedItems: {
            type: Array,
            default: () => []
        },
        toggleButtonText: {
            type: String,
            default: 'Toggle Button'
        },
        toggleButton: {
            type: Boolean,
            default: false
        },
        showToggleButton: {
            type: Boolean,
            default: false
        },
        showApply: {
            type: Boolean,
            default: false
        },
        showCancel: {
            type: Boolean,
            default: false
        },
        isMultiSelect: {
            type: Boolean,
            default: true
        },
        showRadio: {
            type: Boolean,
            default: false
        },
        inputClass: {
            type: String,
            default: 'custom-multiselect-inventory-hub'
        }
    },
    emits: ['update:modelValue', 'openDropdown', 'toggleButtonChange'],
    components: {
        MdTypography,
        MdIconArrowCaret,
        MdButton,
        MdIconReload,
        TextField,
        ToggleSwitch
    },
    setup(props, { emit }) {
        const selectedItems = ref([]);
        const isDropdownOpen = ref(false);
        const dropdownContainer = ref(null);
        const previousSelected = ref([])
        const labelText = ref(null)
        const searchFieldValue = ref('');
        const filteredResults = ref([])

        const actionBtnActive = ref(false);
        const isAllOptionsSelected = computed(() => {
            return (
                (filteredResults.value?.length > 0 &&
                    filteredResults.value.every(option => selectedItems.value.includes(option))) ||
                props.options.length === selectedItems.value?.length
            );
        });

        const toggleDropdown = () => {
            if (props.disabled) {
                return;
            }
            if (isDropdownOpen.value) {
                closeDropdown();
                return;
            }
            isDropdownOpen.value = true;
            previousSelected.value = selectedItems.value
            emit('openDropdown', isDropdownOpen.value);
            if (props.toggleButton) {
                emit('toggleButtonChange', true);
            }
        }

        const isSelectedOption = (option) => {
            return previousSelected.value.some((item) => {
                if (item.submissionId && option.submissionId && item.submissionId === option.submissionId) {
                    return true;
                }

                return item === option;
            });
        };

        const clearSearch = () => {
            searchFieldValue.value = '';
            filteredResults.value = props.options;
        }

        const closeDropdown = () => {
            if (!isDropdownOpen.value) return;
            isDropdownOpen.value = false;
            clearSearch();
            emit('openDropdown', false);
        };

        const handleSearch = () => {
            if (!searchFieldValue.value) {
                return
            }
            filteredResults.value = props.options.filter(option => {
                if (typeof option === 'object' && option.name) {
                    return option.name.toLowerCase().includes(searchFieldValue.value.toLowerCase());
                }

                return option.toLowerCase().includes(searchFieldValue.value.toLowerCase());
            });
        };

        const showEmptySearchResults = () => {
            if (!searchFieldValue.value)
                clearSearch();
        }

        // Helper function to deduplicate items by name property
        const deduplicateItems = (items) => {
            const seen = new Set();
            return items.filter(item => {
                const key = typeof item === 'object' && item.submissionId ? item.submissionId : item;
                if (seen.has(key)) {
                    return false;
                }
                seen.add(key);
                return true;
            });
        };

        // Selects the options on select all
        const toggleSelectAllOption = (e) => {
            if (e.target.checked) {
                previousSelected.value = deduplicateItems([...previousSelected.value, ...filteredResults.value]);
            } else {
                previousSelected.value = previousSelected.value.filter(
                    item => !filteredResults.value.some(filteredItem => {
                        if (typeof item === 'object' && typeof filteredItem === 'object' && item.submissionId && filteredItem.submissionId) {
                            return item.submissionId === filteredItem.submissionId;
                        }
                        return item === filteredItem;
                    })
                );
            }

            selectedItems.value = deduplicateItems(previousSelected.value);
            emit('update:modelValue', selectedItems.value);
        }

        const toggleOption = (option, e) => {
            if (e.target.checked) {
                previousSelected.value = [...previousSelected.value, option];
            } else if (typeof option === 'object') {
                if (option.submissionId) {
                    previousSelected.value = previousSelected.value.filter(p => p.submissionId !== option.submissionId)
                }
                else {
                    previousSelected.value = previousSelected.value.filter((p) => p.value !== option.value);
                }
            }
            selectedItems.value = deduplicateItems(previousSelected.value);
            emit('update:modelValue', selectedItems.value);
        }

        const selectRadioOption = (option) => {
            if (!props.showRadio) return;
            previousSelected.value = [option];
            selectedItems.value = [option];
            actionBtnActive.value = true;
        }

        const applyMapFamily = () => {
            emit('update:modelValue', selectedItems.value);
            toggleDropdown();
            resetDropdown();
        }

        const cancelMapFamily = () => {
            resetDropdown();
            toggleDropdown();
        }

        const resetDropdown = () => {
            previousSelected.value = [];
            selectedItems.value = [];
            emit('update:modelValue', selectedItems.value);
            emit('openDropdown', isDropdownOpen.value);
            actionBtnActive.value = false;
        }

        const selectedOption = (option) => {
            return selectedItems.value.some((item) => {
                if (item.submissionId && option.submissionId && item.submissionId === option.submissionId) {
                    return true;
                }

                return item === option;
            });
        };

        const handleClickOutsideContainer = (event) => {
            // Do nothing if dropdown is already closed
            if (!isDropdownOpen.value) return;

            const container = dropdownContainer.value;
            if (!container) return;

            // Consider both standard contains and composedPath for better compatibility
            const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
            const isInside = container.contains(event.target) || (Array.isArray(path) && path.includes(container));

            if (!isInside) {
                closeDropdown();
            }
        };

        const handleToggleButtonChange = async (value) => {
            emit('toggleButtonChange', value);
            await nextTick();
            resetDropdown();
            handleSearch();
        };

        const getTextLabel = () => {
            if (selectedItems.value.length === 0) {
                labelText.value = props.name
            }
        }

        onMounted(() => {
            window.addEventListener('click', handleClickOutsideContainer);
            getTextLabel()
        });

        onUnmounted(() => {
            window.removeEventListener('click', handleClickOutsideContainer);
        });

        watch(
            () => props.options,
            (newValue) => {
                filteredResults.value = newValue;
            },
            { immediate: true }
        )

        // Watch for changes in the modelValue prop and update previousSelected accordingly
        watch(
            () => props.modelValue,
            (newValue) => {
                previousSelected.value = [...newValue];
            }
        );

        // Watch for changes in the clearSelections prop and reset the dropdown if true
        watch(
            () => props.clearSelections,
            () => {
                if (props?.clearSelections) {
                    resetDropdown()
                }
            }
        )

        // Watch for external changes to selected items and sync internal state
        watch(
            () => props.externalSelectedItems,
            (newValue) => {
                if (newValue && Array.isArray(newValue)) {
                    selectedItems.value = [...newValue];
                    previousSelected.value = [...newValue];
                }
            },
            { deep: true }
        )

        watch(
            () => props.disabled,
            () => {
                if (props.disabled) {
                    isDropdownOpen.value = false;
                    resetDropdown()
                }
            }
        )



        return {
            labelText,
            isSelectedOption,
            getTextLabel,
            previousSelected,
            selectedItems,
            isDropdownOpen,
            dropdownContainer,
            toggleDropdown,
            resetDropdown,
            toggleOption,
            selectedOption,
            toggleSelectAllOption,
            isAllOptionsSelected,
            handleClickOutsideContainer,
            NUMBER_CIRCLE_GLYPH,
            handleSearch,
            searchFieldValue,
            clearSearch,
            showEmptySearchResults,
            filteredResults,
            actionBtnActive,
            no_result_image,
            selectRadioOption,
            applyMapFamily,
            cancelMapFamily,
            handleToggleButtonChange,

        };
    },
};